import EventBus from '../utils/EventBus';
import { IOperateInfo, ESceneMode, IBoxSelectHint, ISelected } from '../types';
import type { IToastOpts, TConfirmOpts } from '@/stores/feedback';
import type { IModelInfo } from '../data/multiModel/multiModel';

/*
  注意：
  1. 事件最好是互斥的，或者关联性低，减少重复调
  2. 下级模块要通知上级模块更新，可以通过事件，上级调下级尽量不要加事件
 */

export enum EditorEventName {
  OPERATE_MODE_CHANGE = 'operateModeChange',
  ZOOM_UPDATE_2D = 'zoomUpdate2d',
  ZOOM_UPDATE_3D = 'zoomUpdate3d',
  SCENE_MODE_CHANGE = 'sceneModeChange',
  HISTORY_CHANGE = 'historyChange',

  // 只有选中id/type/intersectionPoint变化时才会触发
  ENTITY_SELECTED = 'entitySelected',
  REFRESH_SELECTED = 'refreshSelected', // 触发选中渲染刷新

  // 场景移动/缩放
  VIEW2D_CHANGE = 'view2dChange',
  VIEW3D_CHANGE = 'view3dChange',
  // resize
  RESIZE = 'resize',
  // Feedback
  FEEDBACK_CONFIRM = 'feedbackConfirm',
  FEEDBACK_TOAST = 'feedbackToast',

  // clean
  ENTITY_CLEAR = 'entityClear',

  // scheme
  SCHEME_LOADED = 'schemeLoaded',

  // 选中构件参数变化
  PARAM_CHANGE = 'paramChange',

  NOTIFY_MATERIAL_UPDATE = 'notifyMaterialUpdate',

  // 替换相关事件
  REPLACE_START = 'replaceStart',
  REPLACE_REFRESH = 'replaceRefresh',
  REPLACE_END = 'replaceEnd',

  // 自动布局相关
  LAYOUT_START = 'layoutStart',
  LAYOUT_END = 'layoutEnd',

  // 标尺相关
  REFRESH_RULER = 'refreshRuler',

  // 键盘事件
  KEYBOARD_PRESS_UPDATE = 'keyboardPressUpdate',
  KEYBOARD_KEY_PRESS = 'keyboardKeyPress',

  // 对齐相关
  ALIGN_REFERENCE_SELECTED = 'alignReferenceSelected',

  // 控制器相关
  CONTROLLER_REFRESH = 'controllerRefresh',
  /** 把交接瞬间的那一次左键按下补发给刚建好的多选控制器 */
  MULTI_SELECT_TAKEOVER_MOUSE_DOWN = 'multiSelectTakeoverMouseDown',
  /** 框选期间跟随光标的选择模式提示，null 表示当前不在框选 */
  BOX_SELECT_HINT_CHANGE = 'boxSelectHintChange',
  /**
   * 多选进行中的构件列表变化，null 表示已退出多选。
   * 多选期间 selected 是空的（多选态由控制器自己持有），参数面板只能靠这条通知刷新。
   */
  MULTI_SELECT_DATA_CHANGE = 'multiSelectDataChange'
}

/** 全屋布局可以不指定房间（导航栏入口），由界面自行挑；局部布局必然针对某个分区 */
export type TLayoutStartPayload = { type: 'full'; id?: string } | { type: 'partial'; id: string };

export type EditorEventType = {
  [EditorEventName.OPERATE_MODE_CHANGE]: (operateInfo: IOperateInfo) => void;
  [EditorEventName.ZOOM_UPDATE_2D]: (zoom: number) => void;
  [EditorEventName.ZOOM_UPDATE_3D]: (zoom: number) => void;
  [EditorEventName.SCENE_MODE_CHANGE]: (sceneMode: ESceneMode) => void;
  [EditorEventName.HISTORY_CHANGE]: (historyInfo: { undoStackLength: number; redoStackLength: number }) => void;
  [EditorEventName.ENTITY_SELECTED]: (selected: ISelected) => void;
  [EditorEventName.REFRESH_SELECTED]: () => void;
  [EditorEventName.VIEW2D_CHANGE]: () => void;
  [EditorEventName.VIEW3D_CHANGE]: () => void;
  [EditorEventName.RESIZE]: () => void;
  [EditorEventName.FEEDBACK_CONFIRM]: (payload: TConfirmOpts) => void;
  [EditorEventName.FEEDBACK_TOAST]: (payload: IToastOpts) => void;
  [EditorEventName.ENTITY_CLEAR]: (mode: 'furniture' | 'material' | 'all') => void;
  [EditorEventName.SCHEME_LOADED]: () => void;
  [EditorEventName.PARAM_CHANGE]: () => void;
  [EditorEventName.NOTIFY_MATERIAL_UPDATE]: () => void;
  [EditorEventName.REPLACE_START]: () => void;
  [EditorEventName.REPLACE_REFRESH]: () => void;
  [EditorEventName.REPLACE_END]: () => void;
  [EditorEventName.LAYOUT_START]: (payload: TLayoutStartPayload) => void;
  [EditorEventName.LAYOUT_END]: () => void;
  [EditorEventName.REFRESH_RULER]: () => void;
  [EditorEventName.KEYBOARD_PRESS_UPDATE]: () => void;
  [EditorEventName.KEYBOARD_KEY_PRESS]: (key: string) => void;
  [EditorEventName.ALIGN_REFERENCE_SELECTED]: (reference: ISelected) => void;
  [EditorEventName.CONTROLLER_REFRESH]: () => void;
  [EditorEventName.MULTI_SELECT_TAKEOVER_MOUSE_DOWN]: (event: MouseEvent) => void;
  [EditorEventName.BOX_SELECT_HINT_CHANGE]: (hint: IBoxSelectHint | null) => void;
  [EditorEventName.MULTI_SELECT_DATA_CHANGE]: (modelInfos: IModelInfo[] | null) => void;
};

const editorEvent = new EventBus<EditorEventType>();

export default editorEvent;
